import { DOCUMENT } from '@angular/common';
import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
// import {GoogleAnalyticsEventsService} from '../../../services/tracking/google-analytics-events.service';
import { environment } from '@environments/environment';
import { IPermissions } from '@models/Configuration/IPermissions';
import { SessionLayout } from '@models/Sessions/Config';
import { Comment } from '@models/Sessions/Idea/Comment';
import { Idea } from '@models/Sessions/Idea/Idea';
import { Session } from '@models/Sessions/Session';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AssetsPipe } from '@pipes/assets.pipe';
import { AuthenticationService } from '@services/auth/authentication.service';
import { BusService } from '@services/bus.service';
import { ConfigService } from '@services/config.service';
import { EventsService } from '@services/events.service';
import { PdfReportService } from '@services/sessions/pdfreport.service';
import { SessionsRequestService } from '@services/sessions/sessions.request.service';
import { IdeaSocketService } from '@services/sockets/idea.socket.service';
import { SessionSocketService } from '@services/sockets/session.socket.service';
import { filter, filterToRate } from '@utils/ideaFilters';
import { paginationStartFrom } from '@utils/sessionFilters';
import { matchCreatedBy, matchFav, matchTeam } from '@utils/universalFilters';
import _ from 'lodash';

import { TrackingService } from '../../../services/tracking/tracking.service';
import { IdeaSorting } from './idea-bar/idea-bar-controls/idea-bar-controls.component';
import { IdeaModalComponent } from './idea-modal/idea-modal.component';
import { SessionModulesIdeasMatrixComponent } from './modules/ideas-matrix/ideas-matrix.component';
import { MoveIdeasModalComponent } from './move-ideas-modal/move-ideas-modal.component';
import { NewIdeaModalComponent } from './new-idea-modal/new-idea-modal.component';
import { PdfTaskModalComponent } from './pdf-task-modal/pdf-task-modal.component';
import { compareIdeas } from './session.util';

@Component({
  selector: 'app-sessions-single',
  templateUrl: './session.component.html',
  styleUrls: ['./session.component.scss'],
})
export class SessionsSingleComponent implements OnInit, OnDestroy {
  @ViewChild(SessionModulesIdeasMatrixComponent)
  private ideasMatrix: SessionModulesIdeasMatrixComponent;

  @ViewChild('contentStart') contentStart: ElementRef;

  sorting: IdeaSorting = {
    field: 'created_at',
    dir: 'desc',
  };

  isAdmin = false;
  loading = true;
  session: Session;
  layout: SessionLayout;

  currentPage = 0;

  searchFilter = '';
  selectedFilter = 'All projects';
  selectedIdea: Idea;
  nodesData = [];

  ideaFilter = undefined;
  ideaLookup: _.Dictionary<Idea>;

  selectedModule = '';

  showSubSubNav = true;
  permissions: IPermissions;

  autoOpenNIModal = false;
  openIdeaTimeout: any;

  ideaToBeOpened: string = undefined;
  commentToBeOpened: string = undefined;

  scrolled = false;

  featureFlags = environment.featureFlags;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private authService: AuthenticationService,
    private bus: BusService,
    private events: EventsService,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private appConfig: ConfigService,
    private sessionRequest: SessionsRequestService,
    private modalService: NgbModal,
    private sessionSocket: SessionSocketService,
    private ideaSocket: IdeaSocketService,
    private pdfService: PdfReportService,
    private trackingService: TrackingService,
    private assetsPipe: AssetsPipe,
  ) {
    this.permissions = appConfig.permissions;
  }

  get canMoveProjects() {
    return (
      this.featureFlags.topic.moveProjects &&
      this.permissions.session.config.admin(this.session)
    );
  }

  get pageSize() {
    return this.session.config.page_size || 16;
  }

  openNewIdeaModal(forkFrom?: Idea) {
    const modalRef = this.modalService.open(NewIdeaModalComponent, {
      size: 'lg',
      windowClass: 'big-modal',
      beforeDismiss: () => {
        return new Promise((resolve, reject) => {
          if (!confirm('Are you sure you want to discard this idea?')) {
            reject();
          } else {
            resolve(true);
          }
        });
      },
    });
    modalRef.componentInstance.session = this.session;
    modalRef.componentInstance.forkIdea = forkFrom;

    modalRef.result
      .then(() => {
        this.bus.publish(
          this.events.requested.data.session.single,
          this.session._id,
        );
      })
      .catch(() => void 0);
  }

  openMoveIdeasModal() {
    const modalRef = this.modalService.open(MoveIdeasModalComponent, {
      size: 'lg',
      windowClass: 'huge-modal',
    });

    modalRef.componentInstance.session = this.session;
    modalRef.componentInstance.target =
      this.authService.currentUserSync.default_session_id === this.session._id
        ? 'all'
        : 'workflow';
  }

  openReport() {
    this.bus.publish(
      this.events.requested.data.session.report,
      this.session._id,
    );

    this.bus.publish(
      this.events.requested.action.tracking.track,
      this.session,
      'nav',
      'open pdf',
    );
  }

  get teamDict() {
    return _.keyBy(this.session.config.teams, '_id');
  }

  get participantDict() {
    return _.keyBy(this.session.config.participants, '_id');
  }

  get thumbnailImage() {
    if (this.session) {
      return this.assetsPipe.transform(
        this.session.config.thumbnail_id ||
          'image/default_session_thumbnail.png',
      );
    }

    return this.assetsPipe.transform('image/default_session_thumbnail.png');
  }

  get selectedIdeaThumbnail() {
    if (this.selectedIdea) {
      return this.assetsPipe.transform(
        this.selectedIdea.thumbnail_id || 'image/default_idea_thumbnail.png',
      );
    }

    return this.assetsPipe.transform('image/default_idea_thumbnail.png');
  }

  get filteredPaginatedIdeas() {
    return paginationStartFrom<Idea>(
      this.filteredIdeas,
      this.currentPage,
      this.pageSize,
    );
  }

  get filteredIdeas() {
    return this.session.data.ideas
      .filter(i => filter(i, this.searchFilter))
      .filter(i => matchFav(i, this.ideaFilter.filterFav))
      .filter(i =>
        matchCreatedBy(i, this.session.me._id, this.ideaFilter.filterOwn),
      )
      .filter(i =>
        matchTeam(
          i,
          this.participantDict,
          this.ideaFilter.currentSelectedTeam,
          this.ideaFilter.filterTeam,
        ),
      )
      .filter(i =>
        filterToRate(
          i,
          this.participantDict,
          this.ideaFilter.currentUserVotesFor,
          this.ideaFilter.filterToRate,
        ),
      );
  }

  get filteredSortedIdeas() {
    const filtered = this.filteredIdeas;
    if (this.sorting.field === 'none') {
      return filtered;
    } else {
      return filtered.sort((a, b) =>
        compareIdeas(a, b, this.sorting, this.session),
      );
    }
  }

  get filteredSortedPaginatedIdeas() {
    return paginationStartFrom<Idea>(
      this.filteredSortedIdeas,
      this.currentPage,
      this.pageSize,
    );
  }

  openIdea(idea: Idea, comment?: Comment) {
    this.router.navigate(['topics', this.session._id, 'ideas', idea._id], {
      queryParams: {
        edit: this.permissions.session.data.ideas.put(this.session, idea),
      },
    });
    return;
    //
    // NOTE: sometimes this function is called rapidly in succession, while we want to
    // open the modal only once. the timeout is to fix that problem.
    //

    if (!this.openIdeaTimeout) {
      clearTimeout(this.openIdeaTimeout);
      const modalRef = this.modalService.open(IdeaModalComponent, {
        size: 'lg',
        windowClass: 'big-modal',
      });
      (modalRef.componentInstance as IdeaModalComponent).bindData({
        session: this.session,
        idea: idea,
      });

      if (comment) {
        (modalRef.componentInstance as IdeaModalComponent).focusComment(
          comment,
        );
      }

      modalRef.result
        .then(() => {
          this.redrawMatrix();
        })
        .catch(() => {
          this.redrawMatrix();
        });

      this.bus.publish(
        this.events.requested.action.tracking.track,
        this.session,
        'nav',
        'open idea',
        idea.title,
      );
      this.openIdeaTimeout = setTimeout(() => {
        this.openIdeaTimeout = undefined;
      }, 1000);
    }
  }

  redrawMatrix() {
    if (this.ideasMatrix) {
      this.ideasMatrix.redraw();
    }
  }

  tabSelected(tab: string, closeSubnav: boolean) {
    if (this.contentStart) {
      const bodytop = this.docScroll;
      const top = this.contentStart.nativeElement.getBoundingClientRect().top;
      const OFFSET = 48;
      this.document.body.scroll({
        top: top + bodytop - OFFSET,
        behavior: 'smooth',
      });
      this.document.documentElement.scroll({
        top: top + bodytop - OFFSET,
        behavior: 'smooth',
      });
      this.document.scrollingElement.scroll({
        top: top + bodytop - OFFSET,
        behavior: 'smooth',
      });
    }

    if (closeSubnav) {
      this.showSubSubNav = false;
    }

    // prevent action when nothing actually changed
    if (this.selectedModule !== tab) {
      this.selectedModule = tab;
      this.bus.publish(
        this.events.requested.action.tracking.track,
        this.session,
        'nav',
        'open tab',
        tab,
      );
    }
  }

  get docScroll() {
    return Math.max(
      this.document.body.scrollTop,
      this.document.documentElement.scrollTop,
      this.document.scrollingElement.scrollTop,
    );
  }

  toggleSubSubMenu() {
    this.showSubSubNav = !this.showSubSubNav;
  }

  selectIdea(idea: Idea) {
    this.selectedIdea = idea;
  }

  publish(event, idea: Idea) {
    event.preventDefault();

    this.bus.publish(
      this.events.requested.action.session.idea.publish,
      idea,
      this.session,
    );
  }

  followSession() {
    if (!this.session.lockActions) {
      this.session.lockActions = true;
      this.sessionRequest.follow(this.session).subscribe(
        () => {
          this.session.lockActions = false;
          // this.session.data.followers.length;
          this.session.i_follow = true;
        },
        () => {
          this.session.lockActions = false;
        },
      );
    }
  }

  unfollowSession() {
    if (!this.session.lockActions) {
      this.session.lockActions = true;
      this.sessionRequest.unfollow(this.session).subscribe(
        () => {
          this.session.lockActions = false;
          // this.session.data.followers--;
          this.session.i_follow = false;
        },
        () => {
          this.session.lockActions = false;
        },
      );
    }
  }

  toggleDeactivated() {
    this.session.config.deactivated = !this.session.config.deactivated;
    this.saveConfig();
  }

  saveConfig() {
    this.bus.publish(
      this.events.requested.action.session.updateConfig,
      this.session,
    );
  }

  follow(idea: Idea) {
    this.bus.publish(
      this.events.requested.action.session.idea.follow,
      idea,
      this.session,
    );
  }

  unfollow(idea: Idea) {
    this.bus.publish(
      this.events.requested.action.session.idea.unfollow,
      idea,
      this.session,
    );
  }

  currentUserReceive() {
    this.isAdmin = this.authService.isAdmin;
  }

  currentUserError() {
    //
  }

  sessionReceive(session: Session) {
    this.session = session;
    this.session.config.descriptionRich =
      this.session.config.descriptionRich || this.session.config.description;

    if (
      !this.authService.isAdmin &&
      session.me.role > session.config.visibility
    ) {
      alert('You do not have access to see this topic.');
      this.router.navigate(['/']);
    }

    setTimeout(() => (this.loading = false), 200);
    if (!this.session.config.organizer) {
      this.session.config.organizer = this.appConfig.defaultOrganizer;
    }

    this.selectedIdea = undefined;
    this.ideaFilter = {
      selected: 'all',
      currentUserTeam: this.session.me.team,
      currentUserVotesFor: this.session.me,
    };

    this.ideaLookup = _.keyBy(this.session.data.ideas, '_id');

    this.sessionSocket.unsyncUpdates();
    this.sessionSocket.syncUpdates(this.session);
    this.ideaSocket.unsyncUpdates();
    this.ideaSocket.syncUpdates(this.session);

    this.tabSelected('ideas', false);
    this.updateLayout();

    if (this.autoOpenNIModal) {
      this.autoOpenNIModal = false;
      this.openNewIdeaModal();
    } else if (this.ideaToBeOpened) {
      const idea = this.session.data.ideas.find(
        _idea => _idea._id.toString() === this.ideaToBeOpened,
      );
      this.ideaToBeOpened = undefined;

      if (idea) {
        if (this.commentToBeOpened) {
          const comment = idea.comments.find(
            _comment => _comment._id.toString() === this.commentToBeOpened,
          );
          this.commentToBeOpened = undefined;

          this.openIdea(idea, comment);
        } else {
          this.openIdea(idea);
        }
      }
    }
  }

  sessionError() {
    this.router.navigate(['not-found']);
  }

  reportReceive(session) {
    const modal = this.modalService.open(PdfTaskModalComponent, {
      size: 'lg',
      windowClass: 'big-modal',
    });
    modal.componentInstance.task = this.pdfService.open(session);
  }

  reportError(error) {
    console.error(error);
  }

  setNodesData(nodes) {
    (this.nodesData as any) = _.groupBy(nodes, 'created_by');
  }

  setLayout(layout: SessionLayout) {
    this.layout = layout;
    localStorage.setItem(`session-layout-${this.session._id}`, this.layout);
  }

  getLayout() {
    return (
      (localStorage.getItem(
        `session-layout-${this.session._id}`,
      ) as SessionLayout) || undefined
    );
  }

  updateLayout() {
    this.layout = this.getLayout() || this.session.config.layout || 'cards';
  }

  private subscribe() {
    this.bus.subscribe(
      this.events.received.data.user.me.success,
      this.currentUserReceive,
      this,
    );
    this.bus.subscribe(
      this.events.received.data.user.me.failure,
      this.currentUserError,
      this,
    );
    this.bus.subscribe(
      this.events.received.data.session.single.success,
      this.sessionReceive,
      this,
    );
    this.bus.subscribe(
      this.events.received.data.session.single.failure,
      this.sessionError,
      this,
    );
    this.bus.subscribe(
      this.events.received.data.session.report.success,
      this.reportReceive,
      this,
    );
    this.bus.subscribe(
      this.events.received.data.session.report.failure,
      this.reportError,
      this,
    );
    this.bus.subscribe(
      this.events.requested.action.session.updateConfig,
      this.updateLayout,
      this,
    );
  }

  private unsubscribe() {
    this.bus.unsubscribe(
      this.events.received.data.user.me.success,
      this.currentUserReceive,
    );
    this.bus.unsubscribe(
      this.events.received.data.user.me.failure,
      this.currentUserError,
    );
    this.bus.unsubscribe(
      this.events.received.data.session.single.success,
      this.sessionReceive,
    );
    this.bus.unsubscribe(
      this.events.received.data.session.single.failure,
      this.sessionError,
    );
    this.bus.unsubscribe(
      this.events.received.data.session.report.success,
      this.reportReceive,
    );
    this.bus.unsubscribe(
      this.events.received.data.session.report.failure,
      this.reportError,
    );
    this.bus.unsubscribe(
      this.events.requested.action.session.updateConfig,
      this.updateLayout,
    );
  }

  ngOnInit() {
    this.subscribe();

    this.activatedRoute.params.subscribe(params => {
      this.bus.publish(this.events.requested.data.session.single, params.id);
      this.bus.publish(this.events.ui.context.topic, params.id);
    });

    this.activatedRoute.queryParams.subscribe(params => {
      if (
        params.newIdea &&
        (params.newIdea === true || params.newIdea === 'true')
      ) {
        //
        // so we need to open the new idea modal, but not right away.
        // we will set this flag to open the new idea modal the first time that
        // we receive a proper session object from the server.
        //
        this.autoOpenNIModal = true;
      } else if (params.idea) {
        //
        // so we need to open the idea modal of a specific idea, perhaps
        // focusing on a specific comment as well.
        //
        this.ideaToBeOpened = params.idea;
        if (params.comment) {
          this.commentToBeOpened = params.comment;
        }
      }
    });
  }

  ngOnDestroy() {
    this.bus.publish(this.events.ui.context.topic, null);
    this.sessionSocket.unsyncUpdates();
    this.ideaSocket.unsyncUpdates();
    this.unsubscribe();
  }
}
